import { useEffect } from 'react';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { faDoorClosed } from '@fortawesome/pro-regular-svg-icons';
import { AlertBanner, AlertBannerStatus, FormLabel, FormRadio, FormSelect, FormToggle, RadioGroup, TextField } from '@tryhackme/thm-ui-components';
import { Controller, useFormContext } from 'src/common/form';
import { RoomStatus, RoomType } from 'src/common/enums/rooms';
import { ManageRoomDetail } from 'src/features/manage-room/manage-room.types';
import { SettingCard } from '../../../setting-card';
import { FormRow } from '../../../form-row';
import { StyledBottomAdornment } from '../general-section/general-section.styles';
import { StyledFormToggleContainer, StyledInfoContainer, StyledInfoIcon, StyledInfoText, StyledMaximumTeamSize, StyledMutltipleFieldsContainer, StyledRadioGroup, StyledRoomStatus, StyledRoomStatusContainer, StyledRoomStatusEllipse, StyledRoomStatusText, StyledSectionContainer, StyledSelectContainer, StyledSelectLabel } from './room-settings-section.styles';
import { DifficultyRadioInputs, ScoreType } from './room-settings-section.constants';
import { RoomDetail } from './room-settings-section.types';
export const RoomSettingsSection = ({
  roomDetails
}: {
  roomDetails: ManageRoomDetail;
}) => {
  const {
    register,
    control,
    watch,
    setValue
  } = useFormContext<RoomDetail>();
  const roomStatus = roomDetails?.status;
  const showAccessInfoIcon = [RoomStatus.SUBMITTED, RoomStatus.EVALUATING, RoomStatus.READY].includes(roomStatus);
  let statusEndingText = 'has been submitted';
  if (roomStatus === RoomStatus.READY) {
    statusEndingText = 'is ready';
  }
  if (roomStatus === RoomStatus.EVALUATING) {
    statusEndingText = 'has being evaluated';
  }
  const isTeamsEnabled = watch('teams');
  const areChangesLocked = watch('teamSettings.lockChanges');
  const isLimitTeamSizeEnabled = watch('teamSettings.limitTeamSize');
  useEffect(() => {
    if (!isTeamsEnabled) {
      setValue('teamSettings.lockChanges', false);
      setValue('teamSettings.limitTeamSize', false);
      setValue('teamSettings.studentsOnly', false);
    }
  }, [isTeamsEnabled, setValue]);
  return <StyledSectionContainer aria-label="General Room Settings" data-sentry-element="StyledSectionContainer" data-sentry-component="RoomSettingsSection" data-sentry-source-file="room-settings-section.tsx">
      <SettingCard icon={faDoorClosed} title="Room Settings" data-sentry-element="SettingCard" data-sentry-source-file="room-settings-section.tsx">
        <FormRow heading="Status" description="This is the status of your room." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <StyledRoomStatusContainer data-sentry-element="StyledRoomStatusContainer" data-sentry-source-file="room-settings-section.tsx">
            <StyledRoomStatus $status={roomStatus} data-sentry-element="StyledRoomStatus" data-sentry-source-file="room-settings-section.tsx">
              <StyledRoomStatusEllipse $status={roomStatus} data-sentry-element="StyledRoomStatusEllipse" data-sentry-source-file="room-settings-section.tsx" />
              <StyledRoomStatusText data-sentry-element="StyledRoomStatusText" data-sentry-source-file="room-settings-section.tsx">
                {roomStatus === RoomStatus.UNRELEASED ? 'Not Submitted' : roomStatus}
              </StyledRoomStatusText>
            </StyledRoomStatus>
          </StyledRoomStatusContainer>
        </FormRow>

        <FormRow heading="Difficulty" description="How difficult is the room?" data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller control={control} name="difficulty" render={({
          field: {
            onChange,
            value
          }
        }) => <RadioGroup legend="">
                <StyledRadioGroup>
                  {DifficultyRadioInputs.map(input => <FormRadio key={input.id} onChange={onChange} id={input.id} label={input.label} value={input.value} name={input.name} checked={input.value === value} />)}
                </StyledRadioGroup>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Cloning" description="A cloneable room means anyone who joins your room can clone and repurpose it." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="cloneable" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledFormToggleContainer>
                <FormToggle aria-labelledby="room-is-cloneable" checked={value} onCheckedChange={onChange} />
                <FormLabel id="room-is-cloneable" variant="toggle">
                  Enable cloning
                </FormLabel>
              </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Access" description="Rooms can be private, where only users with the room link can join and see room content; or public, where everyone on TryHackMe can join. Public rooms are reviewed before going live." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <div>
            <Controller name="public" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <StyledFormToggleContainer>
                  <FormToggle disabled={showAccessInfoIcon} aria-labelledby="toggle-room-access" checked={value} onCheckedChange={onChange} data-testid="public-access-toggle" />
                  <FormLabel id="toggle-room-access" variant="toggle">
                    Publicly accessible
                  </FormLabel>
                </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />

            {showAccessInfoIcon && <StyledInfoContainer>
                <StyledInfoIcon icon={faCircleInfo} />
                <StyledInfoText>
                  {`This setting is disabled because the status of your room ${statusEndingText}.`}
                </StyledInfoText>
              </StyledInfoContainer>}
          </div>
        </FormRow>

        <FormRow heading="Teams" description="Allow participants to compete in teams. Solo users can still participate, but their results won’t be displayed on the leaderboard." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="teams" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledMutltipleFieldsContainer>
                <StyledFormToggleContainer>
                  <FormToggle aria-labelledby="room-support-teams" checked={value} onCheckedChange={onChange} />
                  <FormLabel id="room-support-teams" variant="toggle">
                    Enable teams
                  </FormLabel>
                </StyledFormToggleContainer>

                {isTeamsEnabled && <>
                    <Controller name="teamSettings.studentsOnly" control={control} render={({
              field: {
                onChange: onFieldChange,
                value: fieldValue
              }
            }) => <StyledFormToggleContainer>
                          <FormToggle aria-labelledby="enable-students-only" checked={fieldValue} onCheckedChange={onFieldChange} />
                          <FormLabel id="enable-students-only" variant="toggle">
                            Limit to students only
                          </FormLabel>
                        </StyledFormToggleContainer>} />
                    <Controller name="teamSettings.limitTeamSize" control={control} render={({
              field: {
                onChange: onFieldChange,
                value: fieldValue
              }
            }) => <StyledFormToggleContainer>
                          <FormToggle aria-labelledby="limit-team-size" checked={fieldValue} onCheckedChange={onFieldChange} />
                          <FormLabel id="limit-team-size" variant="toggle">
                            Limit team size
                          </FormLabel>
                        </StyledFormToggleContainer>} />
                    {isLimitTeamSizeEnabled && <>
                        <StyledMaximumTeamSize>
                          <TextField {...register('teamSettings.maximumTeamSize')} id="maximum-team-size" data-testid="maximum-team-size" variant="input" type="number" label="Maximum team size" defaultValue={5} min={0} />
                        </StyledMaximumTeamSize>
                        <StyledBottomAdornment>
                          Teams exceeding the defined size will not be displayed in the Chart or Leaderboard.
                        </StyledBottomAdornment>
                      </>}

                    <Controller name="teamSettings.lockChanges" control={control} render={({
              field: {
                onChange: onFieldChange,
                value: fieldValue
              }
            }) => <StyledFormToggleContainer>
                          <FormToggle aria-labelledby="lock-team-changes" checked={fieldValue} onCheckedChange={onFieldChange} />
                          <FormLabel id="lock-team-changes" variant="toggle">
                            Block team changes
                          </FormLabel>
                        </StyledFormToggleContainer>} />
                    {areChangesLocked && <AlertBanner status={AlertBannerStatus.INFO} message="A start and end date must be set for tasks. If enabled, team members will no longer be able to make changes to the team structure for the duration of the challenge." />}
                  </>}
              </StyledMutltipleFieldsContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Reset progress" description="Users will be able to reset progress in this room." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="options.isResetProgressEnabled" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledFormToggleContainer>
                <FormToggle aria-labelledby="room-reset-progress" checked={value} onCheckedChange={onChange} />
                <FormLabel id="room-reset-progress" variant="toggle">
                  Enable reset progress
                </FormLabel>
              </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Simple room" description="Simple rooms only contain the tasks and room branding. Useful for rooms that are just for teaching where question points don't matter." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="simpleRoom" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledFormToggleContainer>
                <FormToggle aria-labelledby="room-is-simple" checked={value} onCheckedChange={onChange} />
                <FormLabel id="room-is-simple" variant="toggle">
                  Simple room
                </FormLabel>
              </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Type" description="A challenge room gives students more points per question; a walkthrough room gives less." data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="type" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <RadioGroup legend="">
                <StyledRadioGroup>
                  <FormRadio onChange={onChange} id="challenge" checked={RoomType.CHALLENGE === value} label="Challenge" value="challenge" name="type" />
                  <FormRadio checked={RoomType.WALKTHROUGH === value} onChange={onChange} id="walkthrough" label="Walkthrough" value="walkthrough" name="type" />
                </StyledRadioGroup>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Who can join?" description="Is your room for TryHackMe subscribers, or can anyone join?" data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="join" control={control} render={({
          field: {
            value,
            onChange
          }
        }) => <RadioGroup legend="">
                <StyledRadioGroup>
                  <FormRadio checked={value === 'free'} onChange={onChange} id="free" label="Free" value="free" name="join" />
                  <FormRadio checked={value === 'subscriber'} onChange={onChange} id="subscriber-only" label="Subscribers-only" value="subscriber" name="join" />
                  <FormRadio checked={value === 'business'} onChange={onChange} id="business-only" label="Business-only" value="business" name="join" />
                </StyledRadioGroup>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Lock room " description={`Locking a room is where room-users can join your room, but can't see
           any content (no tasks, chart, scoreboard, etc..) 
          This is useful for running assessments or having users join a room before an event.`} data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="locked" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledFormToggleContainer>
                <FormToggle aria-labelledby="room-is-locked" checked={value} onCheckedChange={onChange} />
                <FormLabel id="room-is-locked" variant="toggle">
                  Locked room
                </FormLabel>
              </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Scoring type" description={`No matter the type (challenge/walkthrough), 
        decide which scoring type your room uses. 
        First blood means the user who answers the question first gets more points.`} data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="scoreType" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledSelectContainer>
                <StyledSelectLabel>For correct answers give:</StyledSelectLabel>
                <FormSelect selected={ScoreType[value]} isSearchable={false} options={Object.values(ScoreType)} handleSelection={selectedOption => onChange(Number(selectedOption?.value))} />
              </StyledSelectContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>

        <FormRow heading="Connection options" description={`Remove the “Start AttackBox” button and connection information in rooms; 
          used if a room does not require a connection to a machine on the TryHackMe network.`} showBottomLine={false} data-sentry-element="FormRow" data-sentry-source-file="room-settings-section.tsx">
          <Controller name="options.remAttackBoxBtn" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <StyledFormToggleContainer>
                <FormToggle aria-labelledby="connection-options" checked={value} onCheckedChange={onChange} />
                <FormLabel id="connection-options" variant="toggle">
                  Remove connection options
                </FormLabel>
              </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="room-settings-section.tsx" />
        </FormRow>
      </SettingCard>
    </StyledSectionContainer>;
};